<template>
  <div v-if="ready" v-hotkey="keymap" class="lesson-chat">
    <div ref="chatList" class="lesson-chat__messages">
      <div v-if="!messages.length" class="lesson-chat__empty">
        <span><v-icon size="29">mdi-message-text-outline</v-icon></span>
        <strong>Повідомлень ще немає</strong>
        <p>{{ emptyChatText }}</p>
      </div>
      <v-list-item
        :class="{
          'lesson-chat__message--current': message.isCurrentUser,
          'lesson-chat__message--other': !message.isCurrentUser,
        }"
        v-for="(message, index) in messages"
        :key="message.id"
        class="lesson-chat__message"
      >
        <v-list-item-content>
          <Message
            :message="message"
            @hook:mounted="messageMounted(index)"
            @messageEvent="messageEventHandler"
          />
        </v-list-item-content>
      </v-list-item>
    </div>
    <section class="lesson-chat__composer">
      <div class="lesson-chat__editor">
        <vue-editor
          ref="messageEditor"
          v-model="newMessage"
          :editorToolbar="customToolbar"
          placeholder="Напишіть повідомлення…"
        />
      </div>
      <div class="lesson-chat__actions">
        <v-file-input
          class="lesson-chat__file"
          dense
          hide-details="auto"
          outlined
          prepend-icon="mdi-paperclip"
          show-size
          :rules="rules"
          v-model="file"
          :loading="false"
          label="Прикріпити файл"
          accept="image/jpeg,image/png,image/jpg,audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/aac,.aac,.mp3,.wav,.ogg,.m4a,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.pdf,.xml,.txt"
        />
        <button
          type="button"
          class="lesson-chat__send"
          :disabled="!canSendMessage || sending"
          @click="sendMessage"
        >
          <span>Надіслати</span>
          <v-icon size="19">mdi-send</v-icon>
        </button>
      </div>
      <small class="lesson-chat__hint">Максимальний розмір файла — 5 МБ</small>
    </section>
    <v-snackbar
      v-model="showSendError"
      class="lesson-chat__error-toast"
      :timeout="6000"
      bottom
      right
      multi-line
      role="alert"
    >
      <div class="lesson-chat__error-toast-content">
        <v-icon class="lesson-chat__error-toast-icon" size="22">
          mdi-alert-circle-outline
        </v-icon>
        <span>Не вдалося надіслати повідомлення. Спробуйте ще раз пізніше.</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn class="lesson-chat__error-toast-retry" text v-bind="attrs" @click="sendMessage">
          Повторити
        </v-btn>
        <v-btn
          class="lesson-chat__error-toast-close"
          icon
          v-bind="attrs"
          aria-label="Закрити повідомлення про помилку"
          @click="showSendError = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
  <div v-else class="lesson-chat__loading">
    <v-progress-circular class="theme-loader" indeterminate size="38" width="4" />
    <span>Завантажуємо повідомлення…</span>
  </div>
</template>

<script>
import Message from '@/components/View/Chat/Message';
import messageEventEnum from '@/enums/messageEventEnum';
import { VueEditor } from 'vue2-editor';
export default {
  name: 'Chat',
  components: { Message, VueEditor },
  props: {
    fromUser: {
      type: Object,
      required: false,
      default: function () {
        return {};
      },
    },
    toUser: {
      type: Object,
      required: false,
      default: function () {
        return {};
      },
    },
    chatId: [String, Number],
  },
  data() {
    return {
      timeoutId: null,
      newMessage: '',
      file: null,
      rules: [
        (value) => {
          return (
            !value ||
            !value.size ||
            value.size < 5000000 ||
            'Розмір файлу має бути менше 5 MB!'
          );
        },
      ],
      ready: false,
      sending: false,
      showSendError: false,
      //https://github.com/davidroyer/vue2-editor/blob/master/src/helpers/default-toolbar.js
      customToolbar: [
        [{ header: [false, 1, 2, 3, 4, 5, 6] }],
        ['bold', 'italic', 'underline', 'strike'], // toggled buttons
        [
          { align: '' },
          { align: 'center' },
          { align: 'right' },
          { align: 'justify' },
        ],
        ['blockquote', 'code-block'],
        [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
        [{ indent: '-1' }, { indent: '+1' }], // outdent/indent
        [{ color: [] }, { background: [] }], // dropdown with defaults from theme
        ['link'], //'image', 'video'
        ['clean'], // remove formatting button
      ],
    };
  },
  computed: {
    emptyChatText() {
      const isTeacherApp = this.$store.getters['ConfigStore/isTeacherApp'];
      return isTeacherApp
        ? 'Напишіть учню перше повідомлення щодо цього уроку.'
        : 'Напишіть вчителю перше повідомлення щодо цього уроку.';
    },
    hasMessageText() {
      const container = document.createElement('div');
      container.innerHTML = this.newMessage || '';
      const text = container.textContent || container.innerText || '';
      return text.replace(/[\u00a0\u200b-\u200d\ufeff]/g, ' ').trim().length > 0;
    },
    hasValidFile() {
      return Boolean(this.$_validate(this.file));
    },
    canSendMessage() {
      return this.hasMessageText || this.hasValidFile;
    },
    keymap() {
      return {
        enter: this.sendMessage,
      };
    },
    messages() {
      return this.$store.getters['ChatStore/getMessages'];
    },
  },
  watch: {
    async messages() {
      await this.waite(100);
      this.$_setBottom();
    },
  },
  async created() {
    await this.$store.dispatch('ChatStore/initChat', {
      chatId: this.chatId,
      fromUser: this.fromUser,
      toUser: this.toUser,
    });
    await this.$store.dispatch('ChatStore/startActualizeChat');
    this.ready = true;
  },
  async mounted() {
    await this.waiteRenderd();
    this.$_setBottom();
  },
  destroyed() {
    this.$store.dispatch('ChatStore/stopActualizeChat');
  },
  methods: {
    waite(time) {
      return new Promise((resolve) => {
        if (this.timeoutId) {
          clearTimeout(this.timeoutId);
          this.timeoutId = null;
        }
        this.timeoutId = setTimeout(resolve, time);
      });
    },
    async waiteRenderd() {
      await this.waite(100);
      const chatList = this.$_getChatList();
      if (chatList) {
        return;
      }
      return this.waiteRenderd();
    },
    updateSubjects() {
      const isTeacherApp = this.$store.getters['ConfigStore/isTeacherApp'];
      if (isTeacherApp) {
        return this.$store.dispatch('TeacherRoomStore/loadTeacherSubjects', {});
      } else {
        return this.$store.dispatch('CourseItemsStore/getItems', {});
      }
    },
    messageEventHandler(payload) {
      const { type, data } = payload;
      switch (type) {
        case messageEventEnum.REMOVE_MESSAGE:
          this.removeMessage(data.id);
          break;
        default:
          console.log(`unsuported type message event type ${type}`);
      }
    },
    async messageMounted(index) {
      if (index !== this.messages.length - 1) {
        return;
      }
      const unreadIds = this.messages.reduce((ids, message) => {
        if (!message.isRead && !message.isCurrentUser) {
          ids.push(message.id);
        }
        return ids;
      }, []);

      if (unreadIds.length === 0) {
        return;
      }
      await this.$store.dispatch('ChatStore/markAsRead', {
        ids: unreadIds,
      });
      await this.updateSubjects();
    },
    $_setBottom() {
      const chatList = this.$_getChatList();
      chatList.scrollTop = 100000;
    },
    $_getChatList() {
      return this.$refs.chatList;
    },
    $_validate(file) {
      const isValid =
        file &&
        file.size &&
        this.rules.every((rule) => {
          return typeof rule(this.file) !== 'string';
        });
      return isValid;
    },
    async sendMessage() {
      if (!this.canSendMessage || this.sending) {
        return;
      }

      const file = this.$_validate(this.file) ? this.file : {};
      this.sending = true;
      this.showSendError = false;

      try {
        await this.$store.dispatch('ChatStore/sendMessages', {
          from: {
            text: this.hasMessageText ? this.newMessage : '',
            file,
          },
          to: {},
        });
        this.newMessage = '';
        this.file = null;
        await this.$nextTick();

        const editor = this.$refs.messageEditor;
        if (editor && editor.quill) {
          editor.quill.setText('', 'silent');
        }
      } catch (error) {
        this.showSendError = true;
      } finally {
        this.sending = false;
      }
    },
    removeMessage(messageId) {
      this.$store.dispatch('ChatStore/removeMessageById', {
        messageId,
      });
    },
  },
};
</script>

<style lang="less" scoped>
.lesson-chat {
  background: var(--theme-app-background-accent);
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.lesson-chat__messages {
  display: flex;
  flex-direction: column;
  gap: 10px;
  height: 52vh;
  max-height: 470px;
  min-height: 300px;
  overflow-y: auto;
  padding: 20px;
}
.lesson-chat__error-toast-content {
  align-items: flex-start;
  display: flex;
  gap: 10px;
  line-height: 1.4;
}
.lesson-chat__error-toast::v-deep .v-snack__wrapper {
  background: var(--theme-banner-background) !important;
  border: 1px solid var(--theme-banner-border);
  border-left: 4px solid var(--theme-header-background);
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(23, 32, 51, 0.14);
  color: var(--theme-banner-text);
}
.lesson-chat__error-toast::v-deep .v-snack__content {
  padding: 14px 16px;
}
.lesson-chat__error-toast-icon,
.lesson-chat__error-toast-retry,
.lesson-chat__error-toast-close::v-deep .v-icon {
  color: var(--theme-header-background) !important;
}
.lesson-chat__error-toast-retry {
  font-weight: 600;
}
.lesson-chat__message {
  align-self: flex-start;
  background: #fff;
  border: 1px solid #e3e8e5;
  border-radius: 14px 14px 14px 4px;
  box-shadow: 0 3px 12px rgba(31, 41, 55, 0.05);
  flex: 0 0 auto;
  min-height: 0;
  padding: 11px 13px;
  max-width: 440px;
  width: 78%;
}
.lesson-chat__message--current {
  align-self: flex-end;
  background: var(--theme-surface-decorative);
  border-color: var(--theme-border-decorative);
  border-radius: 14px 14px 4px 14px;
}
.lesson-chat__message::v-deep .v-list-item__content {
  overflow: visible;
  padding: 0;
}
.lesson-chat__empty {
  align-items: center;
  color: #667085;
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  min-height: 250px;
  padding: 30px;
  text-align: center;
}
.lesson-chat__empty > span {
  align-items: center;
  background: var(--theme-surface-decorative);
  border-radius: 14px;
  display: flex;
  height: 58px;
  justify-content: center;
  margin-bottom: 13px;
  width: 58px;
}
.lesson-chat__empty > span::v-deep .v-icon {
  color: var(--theme-banner-text) !important;
}
.lesson-chat__empty strong {
  color: #344054;
  font-size: 16px;
}
.lesson-chat__empty p {
  font-size: 13px;
  margin: 5px 0 0;
  max-width: 300px;
}
.lesson-chat__composer {
  background: #fff;
  border-top: 1px solid #e3e8e5;
  padding: 14px 18px 16px;
}
.lesson-chat__editor {
  border: 1px solid #d9e0dc;
  border-radius: 12px;
  overflow: hidden;
}
.lesson-chat__editor:focus-within {
  border-color: var(--theme-accent-decorative);
  box-shadow: 0 0 0 3px var(--theme-surface-decorative);
}
.lesson-chat__editor::v-deep .ql-toolbar {
  background: #f8faf9;
  border: 0;
  border-bottom: 1px solid #e7ebe9;
  padding: 6px 8px;
}
.lesson-chat__editor::v-deep .ql-container {
  border: 0;
  font-size: 14px;
  min-height: 86px;
}
.lesson-chat__editor::v-deep .ql-editor {
  max-height: 160px;
  min-height: 86px;
  padding: 12px 14px;
}
.lesson-chat__actions {
  align-items: flex-start;
  display: flex;
  gap: 12px;
  margin-top: 12px;
}
.lesson-chat__file {
  flex: 1;
  margin: 0 !important;
  padding: 0 !important;
}
.lesson-chat__file::v-deep .v-input__slot {
  min-height: 44px !important;
}
.lesson-chat__file::v-deep .v-label {
  font-size: 13px;
}
.lesson-chat__send {
  align-items: center;
  background: var(--theme-header-background);
  border: 0;
  border-radius: 10px;
  color: var(--theme-header-text);
  cursor: pointer;
  display: flex;
  font-size: 14px;
  font-weight: 800;
  gap: 8px;
  justify-content: center;
  min-height: 44px;
  padding: 9px 17px;
}
.lesson-chat__send:hover {
  filter: brightness(0.9);
}
.lesson-chat__send::v-deep .v-icon {
  color: currentColor !important;
}
.lesson-chat__send:disabled,
.lesson-chat__send:disabled:hover {
  background: var(--theme-header-background);
  filter: none;
  cursor: not-allowed;
  opacity: 0.48;
}
.lesson-chat__hint {
  color: #98a2b3;
  display: block;
  font-size: 10px;
  margin-top: 5px;
}
.lesson-chat__loading {
  align-items: center;
  color: #667085;
  display: flex;
  flex-direction: column;
  gap: 13px;
  justify-content: center;
  min-height: 480px;
}
.lesson-chat__messages::-webkit-scrollbar {
  width: 6px;
}
.lesson-chat__messages::-webkit-scrollbar-track {
  background: transparent;
}
.lesson-chat__messages::-webkit-scrollbar-thumb {
  background: #b9d8cb;
  border-radius: 999px;
}
@media (max-width: 600px) {
  .lesson-chat {
    height: calc(100vh - 73px);
  }
  .lesson-chat__messages {
    flex: 1;
    height: auto;
    min-height: 0;
    padding: 14px 12px;
  }
  .lesson-chat__message {
    width: 88%;
  }
  .lesson-chat__composer {
    padding: 11px 12px 12px;
  }
  .lesson-chat__editor::v-deep .ql-toolbar {
    overflow-x: auto;
    white-space: nowrap;
  }
  .lesson-chat__actions {
    align-items: stretch;
    flex-direction: column;
    gap: 8px;
  }
  .lesson-chat__send {
    width: 100%;
  }
}
</style>
